/**
 * A `data-change` part: one write the agent made without asking, because it
 * can be put back exactly. Says what changed and where, and offers Undo
 * (refused when something else has moved the state on since).
 */
import { AxiosError } from "axios";
import { AlertTriangle, CircleCheck, Undo2 } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";
import { AgentAPI } from "@/features/agents/api/agentApi";
import { useAttachmentScope } from "@/features/agents/attachments/AttachmentScopeContext";
import type { AgentChange } from "@/features/agents/types";
import { invalidateClient } from "@/features/clientProfile/lib/clientInvalidation";
import {
  INTAKE_TEMPLATES_TARGET,
  notifyIntakeTemplatesInvalidated
} from "@/features/firmKnowledge/intakeTemplateEvents";
import { notifySettingsInvalidated } from "@/features/onboarding/settingsEvents";
import { notifyTimeInvalidated } from "@/features/timeTracking/timeEvents";
import { cn } from "@/lib/utils";

import { useCaseIdeOptional } from "../../context/CaseIdeContext";
import { titleCase } from "../../header/uscisModel";
import type { InvalidateTarget } from "../../types";
import { RenameTable } from "./RenameTable";

const SCOPE_NOUN: Record<string, string> = {
  case: "Case",
  client: "Client",
  practice: "Practice"
};

/** Changes to the member's or the firm's settings: no scope to name. */
const SETTINGS_KINDS = new Set([
  "lawyer_settings",
  "firm_settings",
  "agent_switches"
]);

/** Which settings caches an undone settings change leaves stale. */
export const settingsStaleAfterUndo = (kind: string): string[] => {
  switch (kind) {
    case "lawyer_settings":
      return [
        "onboarding",
        "profile",
        "session",
        "notification_preferences",
        "time_settings"
      ];
    case "firm_settings":
      return [
        "onboarding",
        "law_firm",
        "agents_md",
        "time_firm_settings",
        "firm_permissions"
      ];
    case "agent_switches":
      return ["onboarding", "law_firm", "notification_preferences"];
    default:
      return [];
  }
};

/** Which caches an undone change of this kind leaves stale. */
export const staleAfterUndo = (kind: string): InvalidateTarget[] => {
  switch (kind) {
    case "tree":
    case "pdf_edit":
      return ["fs", "drafts", "packet"];
    case "draft_created":
    case "draft_deleted":
      return ["drafts"];
    case "case_status":
    case "case_deadline":
    case "filing_status":
      return ["case", "cases"];
    case "case_opened":
      return ["cases"];
    case "profile":
    case "client_photo":
      return ["client"];
    case "record":
    case "record_created":
      return ["records"];
    case "reminder_created":
    case "reminder_status":
      return ["reminders"];
    case "time_entry":
      return ["time"];
    case "questions":
      return ["intakes", "activity"];
    default:
      return [];
  }
};

/** A change to one of the firm's intake templates, not a client's intake. */
const TEMPLATE_TARGET_KIND = "intake_template";

/** "re-pointed 2 citations in Cover Letter; updated the list" as one sentence. */
const sentence = (lines: string[]): string => {
  const text = lines.join("; ");
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.`;
};

/**
 * "Case files: 23 changed" or "Time entry updated (0.5h)" as a title-case
 * title and a muted summary; a label without either shape stays whole.
 */
export const splitLabel = (
  raw: string | null | undefined
): { title: string; summary: string | null } => {
  const label = raw || "Change";
  const at = label.indexOf(": ");
  if (at <= 0) {
    const paren = /^(.+?)\s*\(([^()]+)\)$/.exec(label);
    return paren
      ? { title: titleCase(paren[1]), summary: paren[2].trim() }
      : { title: label, summary: null };
  }
  return {
    title: titleCase(label.slice(0, at)),
    summary: label.slice(at + 2).trim() || null
  };
};

const errorText = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { error?: unknown } | undefined;
    if (typeof data?.error === "string") return data.error;
  }
  return error instanceof Error ? error.message : "Undo failed";
};

export type ChangeUndo = {
  change: AgentChange;
  merge: (next: Partial<AgentChange>) => void;
  busy: boolean;
  refusal: string | null;
  undone: boolean;
  canUndo: boolean;
  /** Whose record changed, when it is worth naming. */
  where: string | null;
  undo: () => Promise<void>;
};

/** A change as last seen here, and its Undo (cache refreshes and toasts included). */
export const useChangeUndo = (initial: AgentChange): ChangeUndo => {
  const scope = useAttachmentScope();
  const ide = useCaseIdeOptional();
  // What this view did since it got `initial` (an undo). Once the server
  // reports the change settled (undone, conflict), its word wins.
  const [local, setLocal] = useState<Partial<AgentChange>>({});
  const change: AgentChange =
    initial.status === "applied"
      ? { ...initial, ...local }
      : { ...local, ...initial };
  const merge = (next: Partial<AgentChange>) =>
    setLocal((cur) => ({ ...cur, ...next }));
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const undone = change.status === "undone";
  const canUndo =
    !undone && change.undoable && Boolean(scope) && Boolean(change.thread_id);
  const outsideFocus =
    change.focus_kind &&
    change.target_kind &&
    (change.focus_kind !== change.target_kind ||
      change.focus_id !== change.target_id);
  // Every card names whose record changed; rows recorded before labels
  // existed fall back to the kind and id, and only off the focus.
  const where =
    change.target_label ||
    (outsideFocus &&
    change.target_kind &&
    change.target_id &&
    change.target_kind !== "time" &&
    change.target_kind !== TEMPLATE_TARGET_KIND &&
    !SETTINGS_KINDS.has(change.target_kind)
      ? `${SCOPE_NOUN[change.target_kind] ?? change.target_kind} ${
          change.target_id
        }`
      : null);

  const undo = async () => {
    if (!scope || !change.thread_id) return;
    setBusy(true);
    setRefusal(null);
    try {
      const next = await AgentAPI.undoChange(
        scope,
        change.thread_id,
        change.id
      );
      merge({ ...next, status: next.status ?? "undone" });
      const targets =
        change.target_kind === TEMPLATE_TARGET_KIND
          ? []
          : staleAfterUndo(change.kind);
      notifyTimeInvalidated(targets);
      notifySettingsInvalidated(settingsStaleAfterUndo(change.kind));
      if (change.target_kind === TEMPLATE_TARGET_KIND)
        notifyIntakeTemplatesInvalidated([INTAKE_TEMPLATES_TARGET]);
      if (targets.length) {
        ide?.invalidate(targets);
        if (change.target_kind === "client" && change.target_id)
          invalidateClient(change.target_id, targets);
      }
      toast.success(`Undid: ${change.label}`);
    } catch (error) {
      const text = errorText(error);
      setRefusal(text);
      toast.error(text);
    } finally {
      setBusy(false);
    }
  };

  return { change, merge, busy, refusal, undone, canUndo, where, undo };
};

/** Whether a change has anything to say beyond its one line. */
export const hasChangeDetails = ({
  change,
  undone,
  refusal
}: ChangeUndo): boolean =>
  Boolean(change.relabeled?.length) ||
  Boolean(change.note && !undone) ||
  Boolean(change.follow_on?.length) ||
  Boolean(undone && change.follow_on_undone) ||
  Boolean(change.kind === "tree" && change.renamed_total) ||
  Boolean(refusal);

/** Relabels, the note, follow-on updates, renames and an undo refusal. */
export const ChangeDetails: FC<{ state: ChangeUndo; className?: string }> = ({
  state,
  className
}) => {
  const { change, undone, refusal, merge } = state;
  const relabeled = change.relabeled?.length ?? 0;
  const followOn = change.follow_on ?? [];
  if (!hasChangeDetails(state)) return null;
  return (
    <div
      className={cn("flex min-w-0 flex-col gap-1", className)}
      data-testid="change-details"
    >
      {relabeled ? (
        <div className="flex items-start gap-2 text-sm text-fg-2">
          <AlertTriangle className="mt-0.5 size-3.25 shrink-0 text-warn" />
          <span>
            {relabeled} exhibit label{relabeled === 1 ? "" : "s"} changed.
          </span>
        </div>
      ) : change.note && !undone ? (
        <div className="text-sm text-fg-3">{change.note}</div>
      ) : null}
      {followOn.length ? (
        <div
          data-testid="change-follow-on"
          className={cn("text-sm text-fg-3", undone && "line-through")}
        >
          {sentence(followOn)}
        </div>
      ) : null}
      {undone && change.follow_on_undone ? (
        <div className="text-sm text-fg-3">
          {sentence([change.follow_on_undone])}
        </div>
      ) : null}
      {change.kind === "tree" && change.renamed_total ? (
        <RenameTable change={change} onChange={merge} />
      ) : null}
      {refusal ? (
        <div className="flex items-start gap-1.5 text-sm text-danger">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          <span>{refusal}</span>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Undo, or "Undone" once it is. Shrinks to its icon in a narrow container
 * (an `@container` ancestor).
 */
export const UndoControl: FC<{ state: ChangeUndo; className?: string }> = ({
  state,
  className
}) => {
  const { undone, canUndo, busy, undo } = state;
  if (undone)
    return (
      <span
        className={cn("shrink-0 px-1.5 text-sm text-fg-3", className)}
        data-testid="change-undone"
      >
        Undone
      </span>
    );
  if (!canUndo) return null;
  return (
    <Button
      variant="ghost"
      size="xs"
      className={cn("shrink-0", className)}
      disabled={busy}
      onClick={() => void undo()}
      title="Undo"
      data-testid="undo-change"
    >
      <Undo2 /> <span className="@max-xs:sr-only">Undo</span>
    </Button>
  );
};

type Props = {
  change: AgentChange;
  className?: string;
};

export const ChangeCard: FC<Props> = ({ change: initial, className }) => {
  const state = useChangeUndo(initial);
  const { change, undone, where } = state;
  const { title, summary } = splitLabel(change.label);
  const details = hasChangeDetails(state);

  return (
    <div
      data-testid="change-card"
      data-change-id={change.id}
      data-status={change.status}
      className={cn(
        "@container flex min-w-0 flex-col divide-y divide-line-1 rounded-lg bg-elevated text-base inset-ring inset-ring-line-2",
        undone && "opacity-70",
        className
      )}
    >
      <div className="flex min-h-9 min-w-0 flex-wrap items-center gap-x-2 gap-y-1 py-1 pr-1.5 pl-3">
        {undone ? (
          <Undo2 className="size-3.5 shrink-0 text-fg-3" />
        ) : (
          <CircleCheck className="size-3.5 shrink-0 text-success" />
        )}
        <span
          className={cn(
            "flex min-w-0 flex-1 basis-40 items-baseline gap-2 leading-snug",
            undone && "line-through"
          )}
        >
          <span
            className={cn(
              summary ? "shrink-0 whitespace-nowrap" : "min-w-0 truncate",
              undone ? "text-fg-3" : "text-fg"
            )}
          >
            {title}
          </span>
          {summary ? (
            <span className="min-w-0 truncate text-fg-3">{summary}</span>
          ) : null}
        </span>
        {where ? <Tag data-testid="change-target">{where}</Tag> : null}
        {change.delegated_from ? <Tag>Delegated</Tag> : null}
        <UndoControl state={state} className="ml-auto" />
      </div>
      {details ? <ChangeDetails state={state} className="px-3 py-2" /> : null}
    </div>
  );
};
